import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download, FileText, Headphones, ArrowRight, BookOpen, ShoppingCart } from "lucide-react";
import Reveal from "../components/Reveal";
import BlurImage from "../components/BlurImage";
import api, { apiError } from "../lib/api";
import { startCheckout } from "../lib/razorpay";
import { useAuth } from "../context/AuthContext";
import { useSite } from "../context/SiteContext";
import GuideDownloadModal from "../components/GuideDownloadModal";
import { toast } from "sonner";

const GUIDE_ICONS = { Toolkit: Headphones, Template: FileText, Guide: BookOpen };

export default function Resources() {
    const { user } = useAuth();
    const { openNotify, openPaymentSoon } = useSite();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [guides, setGuides] = useState([]);
    const [activeGuide, setActiveGuide] = useState(null);
    useEffect(() => {
        api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
        api.get("/guides").then((r) => setGuides(r.data)).catch(() => {});
    }, []);

    const priceOf = (p) => p.sale_price || p.price || 0;

    const acquire = async (p) => {
        if (!user) { navigate("/login"); return; }
        const price = priceOf(p);
        if (price === 0) {
            try {
                await api.post(`/products/${p.slug}/get-free`);
                try {
                    const res = await api.get(`/products/${p.slug}/download`, { responseType: "blob" });
                    const url = URL.createObjectURL(res.data);
                    const a = document.createElement("a"); a.href = url; a.download = p.slug; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
                    toast.success("Unlocked! Your download has started.");
                } catch {
                    toast.success("Unlocked! It's now in your library, we'll email the file shortly.");
                }
            } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
            return;
        }
        await startCheckout({
            kind: "product", slug: p.slug,
            onSuccess: async () => {
                try {
                    const res = await api.get(`/products/${p.slug}/download`, { responseType: "blob" });
                    const url = URL.createObjectURL(res.data);
                    const a = document.createElement("a"); a.href = url; a.download = p.slug; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
                } catch { navigate("/dashboard"); }
            },
        });
    };

    return (
        <div data-testid="resources-page">
            <section className="bg-white py-20 lg:py-24 border-b border-[#e8e1d4]">
                <div className="mx-container text-center">
                    <p className="overline mb-5">MUVIARTS Academy · Resources</p>
                    <h1 className="font-display font-semibold tracking-tight text-5xl sm:text-7xl leading-[0.98] max-w-4xl mx-auto">Learn, download, <span className="italic text-brand-gold">grow.</span></h1>
                    <p className="mt-7 max-w-xl mx-auto text-neutral-500 text-lg">Courses, ebooks, templates and guides, free and premium, to help you build your career.</p>
                </div>
            </section>

            {/* Digital products */}
            <section className="py-16 lg:py-24 bg-cream border-b border-[#e8e1d4]">
                <div className="mx-container">
                    <p className="overline mb-6 text-center">Ebooks & Digital Products</p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {products.map((p, i) => (
                            <Reveal key={p.slug} delay={(i % 3) * 0.05}>
                                <div className="bg-white border border-[#e8e1d4] overflow-hidden h-full flex flex-col card-lift" data-testid={`product-card-${p.slug}`}>
                                    <div className="aspect-[16/10] overflow-hidden"><BlurImage src={p.cover} alt={p.title} className="h-full w-full object-cover" /></div>
                                    <div className="p-5 flex flex-col flex-1 text-center items-center">
                                        <p className="text-[11px] uppercase tracking-[0.16em] text-brand-golddark">{p.type}</p>
                                        <h3 className="mt-2 font-display font-semibold text-lg leading-tight">{p.title}</h3>
                                        <p className="mt-2 text-sm text-neutral-500 line-clamp-2 flex-1">{p.description}</p>
                                        <div className="mt-4 flex flex-col items-center gap-3">
                                            <span className="font-display font-semibold">{p.coming_soon ? "Coming soon" : priceOf(p) === 0 ? "Free" : `₹${priceOf(p).toLocaleString("en-IN")}`}</span>
                                            {p.coming_soon ? (
                                                <button onClick={() => openNotify({ kind: "product", slug: p.slug, title: p.title })} className="btn-dark !px-4 !py-2.5 text-xs" data-testid={`product-notify-${p.slug}`}><ShoppingCart className="h-4 w-4" /> Notify Me</button>
                                            ) : (
                                                <button onClick={() => acquire(p)} className="btn-dark !px-4 !py-2.5 text-xs" data-testid={`product-cta-${p.slug}`}>
                                                    {priceOf(p) === 0 ? <><Download className="h-4 w-4" /> Get Free</> : <><ShoppingCart className="h-4 w-4" /> Buy</>}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Free guides */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="mx-container">
                    <p className="overline mb-6 text-center">Free Guides & Templates</p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {guides.map((g, i) => {
                            const Icon = GUIDE_ICONS[g.tag] || FileText;
                            return (
                                <Reveal key={g.slug} delay={(i % 3) * 0.06}>
                                    <div className="group bg-cream border border-[#e8e1d4] p-7 h-full text-center flex flex-col items-center hover:border-ink transition-colors" data-testid={`guide-${g.slug}`}>
                                        <span className="h-12 w-12 rounded-full border border-[#e0d6c2] grid place-items-center group-hover:border-brand-gold group-hover:bg-brand-gold/10 transition-colors">
                                            <Icon className="h-5 w-5 text-brand-golddark" />
                                        </span>
                                        <p className="mt-5 text-[11px] uppercase tracking-[0.16em] text-brand-golddark">{g.tag}</p>
                                        <h3 className="mt-2 font-display font-semibold text-lg">{g.title}</h3>
                                        <p className="mt-2 text-sm text-neutral-500 leading-relaxed flex-1">{g.description}</p>
                                        <button onClick={() => setActiveGuide(g)} className="btn-dark !px-5 !py-2.5 text-xs mt-5" data-testid={`guide-download-${g.slug}`}>
                                            <Download className="h-4 w-4" /> Free Download
                                        </button>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                    <div className="mt-14 text-center">
                        <Link to="/courses" className="btn-primary" data-testid="resources-cta">Explore Courses <ArrowRight className="h-4 w-4" /></Link>
                    </div>
                </div>
            </section>

            <GuideDownloadModal open={!!activeGuide} guide={activeGuide} onClose={() => setActiveGuide(null)} />
        </div>
    );
}
